import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { GraphNode } from '@/features/metrics/model/contracts'
import { usePostsFeed } from '@/features/posts/state/usePostsFeed'
import { PostCard } from '@/features/posts/ui/PostCard'
import { Skeleton } from '@/shared/ui/Skeleton'

interface NodeDetailDrawerProps {
  node: GraphNode | null
  topicId: string
  onClose: () => void
}

function useDrawerEscKey(onClose: () => void, open: boolean) {
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])
}

function NodeTypeBadge({ type, color }: { type: string; color: string }) {
  const { t } = useTranslation('network')
  const label =
    type === 'author' || type === 'source' || type === 'group'
      ? t(`drawer.type.${type}`)
      : type

  return (
    <span className="flex items-center gap-1.5">
      <span
        className="h-3 w-3 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-muted-foreground">
        {label}
      </span>
    </span>
  )
}

function RelatedPostsList({ node }: { node: GraphNode }) {
  const { t } = useTranslation('network')

  const postsQuery =
    node.type === 'author'
      ? { author: node.label, limit: 5 }
      : { search: node.label, limit: 5 }

  const { viewModel } = usePostsFeed({ ...postsQuery, page: 1 })

  if (viewModel.isLoading) {
    return (
      <div className="flex flex-col gap-2 pt-2">
        <Skeleton className="h-20 w-full rounded-xl" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
    )
  }

  if (!viewModel.isLoading && viewModel.posts.length === 0) {
    return <p className="pt-2 text-sm text-muted-foreground">{t('drawer.noRelated')}</p>
  }

  return (
    <div className="flex flex-col gap-2 pt-2">
      {viewModel.posts.slice(0, 5).map((post) => (
        <PostCard key={post.key} post={post} />
      ))}
    </div>
  )
}

export function NodeDetailDrawer(props: NodeDetailDrawerProps) {
  const { node, onClose } = props
  const { t } = useTranslation('network')
  const open = node !== null
  const backdropRef = useRef<HTMLDivElement>(null)

  useDrawerEscKey(onClose, open)

  return (
    <>
      <div
        ref={backdropRef}
        aria-hidden="true"
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/30 transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label={node?.label ?? ''}
        className={cn(
          'fixed right-0 top-0 z-50 flex h-full w-full max-w-sm flex-col bg-card shadow-xl transition-transform duration-200 ease-in-out',
          'border-l border-border',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-4">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="truncate text-base font-semibold text-foreground">
              {node?.label ?? ''}
            </span>
            {node && <NodeTypeBadge type={node.type} color={node.color} />}
            {node && (
              <span className="text-xs text-muted-foreground">
                {t('drawer.influence')}:{' '}
                <span className="font-semibold text-foreground">{node.influence}</span>
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('drawer.close')}
            className="mt-0.5 shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {node && (
            <>
              <h2 className="text-sm font-semibold text-foreground">
                {t('drawer.relatedPosts')}
              </h2>
              <RelatedPostsList node={node} />
            </>
          )}
        </div>
      </aside>
    </>
  )
}
